export function safeParseJSON(value, fallback) {
	if (typeof value !== "string") return fallback;
	try {
		const parsed = JSON.parse(value);
		return parsed ?? fallback;
	} catch {
		return fallback;
	}
}

export function getStorageJSON(key, fallback) {
	if (typeof window === "undefined") return fallback;
	return safeParseJSON(window.localStorage.getItem(key), fallback);
}

export function sanitizeText(value, maxLength = 120) {
	if (typeof value !== "string") return "";
	return value.replace(/[<>]/g, "").trim().slice(0, maxLength);
}

export function isValidEmail(value) {
	if (typeof value !== "string") return false;
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim().toLowerCase());
}

export function formatCurrency(amount) {
	const numeric = Number(amount) || 0;
	return `Rs ${numeric.toFixed(2)}`;
}

export const SERVICEABLE_AREAS = ["Bhayandar", "Mira Road", "Dahisar", "Borivali", "Kandivali", "Malad"];

export function normalizeDeliveryArea(area) {
	const value = String(area || "").trim();
	return SERVICEABLE_AREAS.includes(value) ? value : "Malad";
}

export function getDeliveryChargeByArea(area) {
	const chargeMap = {
		Bhayandar: 65,
		"Mira Road": 55,
		Dahisar: 45,
		Borivali: 35,
		Kandivali: 25,
		Malad: 25,
	};
	return chargeMap[area] ?? chargeMap.Malad;
}

export function getEstimatedDeliveryDate(referenceTime = new Date()) {
	const now = new Date(referenceTime);
	const date = new Date(now);
	const cutoffHour = 22;
	date.setDate(date.getDate() + (now.getHours() >= cutoffHour ? 3 : 1));
	return date;
}

function toDateAtLocalMidnight(value) {
	const date = new Date(value);
	date.setHours(0, 0, 0, 0);
	return date;
}

export function getDeliveryScheduleBounds(now = new Date()) {
	const minDate = toDateAtLocalMidnight(getEstimatedDeliveryDate(now));
	const maxDate = toDateAtLocalMidnight(minDate);
	maxDate.setDate(maxDate.getDate() + 7);
	return { minDate, maxDate };
}

export function toDateInputValue(value) {
	const date = toDateAtLocalMidnight(value);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function resolveScheduledDeliveryDate(dateInput, now = new Date()) {
	const { minDate, maxDate } = getDeliveryScheduleBounds(now);
	if (!dateInput) return minDate;

	const candidate = toDateAtLocalMidnight(new Date(`${dateInput}T00:00:00`));
	if (Number.isNaN(candidate.getTime())) return minDate;
	if (candidate < minDate) return minDate;
	if (candidate > maxDate) return maxDate;
	return candidate;
}

export function calculateCheckoutTotals(items, area) {
	const subtotal = (Array.isArray(items) ? items : []).reduce(
		(acc, item) => acc + (Number(item.price) || 0) * (Number(item.qty) || 0),
		0
	);
	const gst = +(subtotal * 0.05).toFixed(2);
	const delivery = getDeliveryChargeByArea(area);
	const total = +(subtotal + gst + delivery).toFixed(2);

	return {
		subtotal: +subtotal.toFixed(2),
		gst,
		delivery,
		total,
	};
}

export function loadRazorpayScript() {
	return new Promise((resolve) => {
		if (typeof window === "undefined") {
			resolve(false);
			return;
		}

		if (window.Razorpay) {
			resolve(true);
			return;
		}

		const script = document.createElement("script");
		script.src = "https://checkout.razorpay.com/v1/checkout.js";
		script.async = true;
		script.onload = () => resolve(true);
		script.onerror = () => resolve(false);
		document.body.appendChild(script);
	});
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/+$/, "");

function normalizeApiBaseUrl(rawUrl) {
	if (!rawUrl || typeof window === "undefined") return rawUrl;
	const trimmed = String(rawUrl).trim();
	if (!trimmed) return "";

	try {
		const currentProtocol = window.location.protocol;
		const parsed = new URL(trimmed);
		const isHttpOnHttpsPage = currentProtocol === "https:" && parsed.protocol === "http:";
		const isLocalHost = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";

		if (isHttpOnHttpsPage && !isLocalHost) {
			parsed.protocol = "https:";
			return parsed.toString().replace(/\/+$/, "");
		}

		return parsed.toString().replace(/\/+$/, "");
	} catch {
		return trimmed;
	}
}

function resolveApiBaseUrl() {
	if (API_BASE_URL) return normalizeApiBaseUrl(API_BASE_URL);

	if (typeof window !== "undefined") {
		const host = window.location.hostname;
		const isLocalHost = host === "localhost" || host === "127.0.0.1";
		if (isLocalHost) {
			const protocol = window.location.protocol === "https:" ? "https" : "http";
			return `${protocol}://localhost:8080`;
		}
	}

	return "";
}

const RESOLVED_API_BASE_URL = resolveApiBaseUrl();

function buildApiUrl(path) {
	if (!RESOLVED_API_BASE_URL) return path;
	return `${RESOLVED_API_BASE_URL}${path}`;
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 7000) {
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), timeoutMs);
	try {
		return await fetch(url, {
			...options,
			signal: controller.signal,
		});
	} finally {
		clearTimeout(timeout);
	}
}

async function fetchWithRetry(url, options = {}, config = {}) {
	const retries = Number(config.retries ?? 1);
	const timeoutMs = Number(config.timeoutMs ?? 7000);
	const retryDelayMs = Number(config.retryDelayMs ?? 350);

	let lastError = null;
	for (let attempt = 0; attempt <= retries; attempt += 1) {
		try {
			const response = await fetchWithTimeout(url, options, timeoutMs);
			if (!response.ok && response.status >= 500 && attempt < retries) {
				await new Promise((resolve) => setTimeout(resolve, retryDelayMs * (attempt + 1)));
				continue;
			}
			return response;
		} catch (error) {
			lastError = error;
			if (attempt >= retries) throw error;
			await new Promise((resolve) => setTimeout(resolve, retryDelayMs * (attempt + 1)));
		}
	}

	throw lastError || new Error("network-error");
}

export async function verifyPaymentResponse(response) {
	if (!response?.razorpay_payment_id) return false;

	try {
		const verifyRes = await fetchWithRetry(buildApiUrl("/api/verify-payment"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(response),
		}, { timeoutMs: 7000, retries: 1, retryDelayMs: 300 });

		if (!verifyRes.ok) return false;
		const data = await verifyRes.json();
		return Boolean(data?.verified);
	} catch {
		return Boolean(response?.razorpay_payment_id && response?.razorpay_signature);
	}
}

export async function notifyOrderSuccess(payload, idToken = "") {
	const cleanPayload = payload && typeof payload === "object" ? payload : null;
	if (!cleanPayload) return { ok: false, reason: "invalid-payload" };

	try {
		const headers = {
			"Content-Type": "application/json",
		};

		if (idToken) {
			headers.Authorization = `Bearer ${idToken}`;
		}

		const response = await fetchWithRetry(buildApiUrl("/api/order-success-notify"), {
			method: "POST",
			headers,
			body: JSON.stringify(cleanPayload),
		}, { timeoutMs: 7000, retries: 1, retryDelayMs: 300 });

		if (!response.ok) {
			return { ok: false, reason: `http-${response.status}` };
		}

		return { ok: true };
	} catch {
		return { ok: false, reason: "network-error" };
	}
}
